import { ActionIcon, Button, Loader, Menu, SimpleGrid, Text, Tooltip } from '@mantine/core'
import { DatePickerInput, type DatePickerPreset } from '@mantine/dates'
import { useDocumentTitle } from '@mantine/hooks'
import { IconCalendar, IconCheck, IconChevronDown, IconDownload, IconLayoutList, IconPrinter } from '@tabler/icons-react'
import {
  type AppId,
  apps,
  dateFormat,
  dayjs,
  type FilterValue,
  formatDate,
  formatMoney,
  formatMonth,
  formatNumber,
  groupFilter,
  MAX_GROUPS,
  parseGrouping,
  parseListParams,
  periodKeys,
  type Report,
  type ResolvedField,
  reportLabels,
  type SummaryGroup,
  today,
  toSearchParams,
} from '@tnn/core'
import { currentLocale } from '@tnn/i18n'
import { DataGrid, type DataTableSortStatus, Page, Stat } from '@tnn/ui'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { useResourceSummary } from '../api/resources'
import { goToApp } from '../app/go'
import { appLink } from '../app/links'
import { useNavigate } from '../app/scope'
import { useCan } from '../auth/session'
import { GuideButton } from '../guides/guides'
import { PrintArea, PrintableTable, usePrint } from '../print/print'
import { exportSheet } from '../resource/dataio'
import { fieldText } from '../resource/FieldValue'
import { FilterMenu } from '../resource/filters'

const iso = (d: ReturnType<typeof dayjs>) => d.format('YYYY-MM-DD')
const thisMonth = (): FilterValue => ({ op: 'range', from: iso(dayjs().startOf('month')), to: today() })
const dayOf = (v: string | undefined) => (v === 'today' || v === 'now' ? today() : v)

/** A group as a row of the table: its words beside its figures */
interface Row extends SummaryGroup {
  label: string
}

/** One figure of the report: the count, or a sum */
interface Figure {
  name: string | null
  label: string
  of: (g: Pick<SummaryGroup, 'count' | 'sums'>) => number
}

/**
 * A report's page (`defineReport`, docs/reports.md): a period on its date, a grouping, its list's filters; the totals,
 * then a row per group that opens its records in the list. Its state is the URL, as a list's is (`f.<field>`, `by`),
 * so a report is shared as a link. Excel and print take what the screen shows.
 */
export function ReportPage({ report }: { report: Report }) {
  const { resource } = report
  const { t, i18n } = useTranslation()
  const locale = currentLocale(i18n)
  const can = useCan()
  const navigate = useNavigate()
  const [search, setSearch] = useSearchParams()
  const title = t(reportLabels.title(report))
  useDocumentTitle(`${title} · ${t(`apps.${report.app}`)}`)

  const by = report.groups.find((g) => g === search.get('by')) ?? (report.groups[0] as string)
  const grouping = parseGrouping(by)
  const groupField = resource.field(grouping.field) as ResolvedField
  // Always a period: this month until another is chosen
  const filters = useMemo(() => ({ [report.date]: thisMonth(), ...parseListParams(search).filters }), [search, report.date])
  const period = filters[report.date]
  const from = period?.op === 'range' ? dayOf(period.from) : undefined
  const to = period?.op === 'range' ? dayOf(period.to) : undefined
  const update = (next: { filters?: Record<string, FilterValue>; by?: string }) => {
    const params = toSearchParams({ filters: next.filters ?? filters })
    params.set('by', next.by ?? by)
    setSearch(params, { replace: true })
  }
  const onlyPeriod = () => ({ [report.date]: period ?? thisMonth() })

  const asked = useMemo(() => ({ ...filters, ...report.where }), [filters, report.where])
  const query = useResourceSummary(resource, { by, sums: report.sums, filters: asked })
  const summary = query.data
  const refs = summary?.refs ?? {}
  const total = summary?.total

  const fieldLabel = (name: string) => t(reportLabels.field(report, name), { defaultValue: t(resource.field(name)?.label ?? name) })
  const groupingLabel = (g: string) => {
    const { field, period: p } = parseGrouping(g)
    return p ? t(p === 'day' ? 'report.byDay' : 'report.byMonth') : fieldLabel(field)
  }
  const labelOf = (key: string | null) => {
    if (key === null) return t('report.none')
    if (grouping.period === 'month') return formatMonth(key, { locale })
    if (grouping.period === 'day' || groupField.type === 'date') return formatDate(key, { locale })
    return fieldText(groupField, key, { resource, refs, t, locale }) || key
  }
  const figures: Figure[] = [
    { name: null, label: t(reportLabels.count(report), { defaultValue: t('report.count') }), of: (g) => g.count },
    ...report.sums.map((name) => ({ name, label: fieldLabel(name), of: (g: Pick<SummaryGroup, 'sums'>) => g.sums[name] ?? 0 })),
  ]
  const shown = (f: Figure, g: Pick<SummaryGroup, 'count' | 'sums'>) =>
    f.name && resource.field(f.name)?.type === 'money' ? formatMoney(f.of(g), { locale }) : formatNumber(f.of(g), { locale })

  const groups = summary?.groups ?? []
  const quiet = (key: string): SummaryGroup => ({ key, count: 0, sums: {} })
  const ordered =
    grouping.period && from && to && !summary?.more
      ? // Every day or month of the period, the quiet ones too
        periodKeys(grouping.period, from, to).map((key) => groups.find((g) => g.key === key) ?? quiet(key))
      : groupField.options
        ? // Choices in their own order, as the list's chips are
          [...groups].sort((a, b) => rank(groupField, a.key) - rank(groupField, b.key))
        : groups
  const rows: Row[] = ordered.map((g) => ({ ...g, label: labelOf(g.key) }))

  // A sort is the grouping's it was picked for: another grouping starts in its own order
  const [sorting, setSorting] = useState<{ by: string; status: DataTableSortStatus<Row> }>()
  const sort = sorting?.by === by ? sorting.status : undefined
  const sorted = sort ? [...rows].sort(compare(sort, figures, locale)) : rows

  /** Where records with these filters are listed, in the resource's app */
  const listWith = (wanted: Record<string, FilterValue>) => {
    if (!resource.path) return undefined
    const path = `/${resource.path}?${toSearchParams({ filters: wanted })}`
    return resource.app === report.app || !(resource.app in apps) ? path : appLink(resource.app as AppId, path)
  }
  /** A group's records: the report's filters and the group's own (none for a group of no value) */
  const groupHref = (key: string | null) => {
    const own = groupFilter(grouping, key, filters[grouping.field])
    return own ? listWith({ ...asked, [grouping.field]: own }) : undefined
  }
  const open = (href: string | undefined) => {
    if (href?.startsWith('/')) navigate(href)
    else if (href) goToApp(href)
  }

  const periodText = from && to ? `${formatDate(from, { locale })} – ${formatDate(to, { locale })}` : ''
  const filterText = report.filters
    .flatMap((name) => {
      const value = filters[name]
      const field = resource.field(name)
      if (!value || !field) return []
      const values =
        value.op === 'in'
          ? value.values.map((v) => fieldText(field, v, { resource, refs, t, locale })).join(', ')
          : `${formatDate(dayOf(value.from), { locale })} – ${formatDate(dayOf(value.to), { locale })}`
      return [`${t(field.label)}: ${values}`]
    })
    .join(' · ')

  const printer = usePrint({ title: `${title} ${periodText}`, landscape: figures.length > 5, pageLabel: t('print.page') })
  const runExport = () =>
    exportSheet({
      fileName: `${title} ${from ?? ''}–${to ?? ''}`,
      sheet: title,
      rows: [...sorted, ...(total ? [{ key: null, label: t('report.total'), ...total }] : [])],
      columns: [
        { header: groupingLabel(by), value: (r: Row) => r.label, width: 28 },
        ...figures.map((f) => ({
          header: f.label,
          value: (r: Row) => f.of(r),
          type: f.name && resource.field(f.name)?.type === 'money' ? ('money' as const) : ('number' as const),
        })),
      ],
    })

  return (
    <Page
      title={title}
      subtitle={t(reportLabels.description(report))}
      actions={
        <>
          {can('export', resource.key) && resource.actions.includes('export') && (
            <IconButton label={t('actions.export')} onClick={runExport} disabled={!rows.length}>
              <IconDownload size={22} stroke={1.75} />
            </IconButton>
          )}
          {can('print', resource.key) && resource.actions.includes('print') && (
            <IconButton label={t('actions.print')} onClick={() => printer.print()} disabled={!rows.length}>
              <IconPrinter size={22} stroke={1.75} />
            </IconButton>
          )}
          <GuideButton resource={resource} guide={report.key} />
        </>
      }
      toolbar={
        <>
          <PeriodPicker from={from} to={to} onChange={(range) => update({ filters: { ...filters, [report.date]: range } })} />
          <Menu position="bottom-start">
            <Menu.Target>
              <Button
                variant="default"
                size="sm"
                radius="sm"
                flex="none"
                leftSection={<IconLayoutList size={18} stroke={1.75} />}
                rightSection={<IconChevronDown size={14} />}
                aria-label={`${t('report.groupBy')}: ${groupingLabel(by)}`}
              >
                {groupingLabel(by)}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{t('report.groupBy')}</Menu.Label>
              {report.groups.map((g) => (
                <Menu.Item key={g} rightSection={g === by ? <IconCheck size={16} /> : null} onClick={() => update({ by: g })}>
                  {groupingLabel(g)}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
          <FilterMenu
            resource={resource}
            fields={report.filters}
            params={{ page: 1, pageSize: 0, filters }}
            setParams={(p) => update({ filters: { ...p.filters, ...onlyPeriod() } })}
            t={t}
            locale={locale}
          />
          {report.filters.some((name) => filters[name]) && (
            <Button variant="subtle" size="compact-sm" onClick={() => update({ filters: onlyPeriod() })}>
              {t('actions.clearFilters')}
            </Button>
          )}
        </>
      }
    >
      {query.isPending ? (
        <Loader type="dots" m="xl" />
      ) : (
        // As a list: the totals as tiles over the table, which fills the sheet and scrolls inside itself, its total in view
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            opacity: query.isFetching ? 0.6 : 1,
            transition: 'opacity 150ms',
          }}
        >
          <SimpleGrid cols={{ base: 2, sm: 3, lg: Math.min(figures.length, 6) }} spacing="sm" p="8px 24px 16px">
            {figures.map((f) => (
              <Stat
                key={f.label}
                label={f.label}
                value={total ? shown(f, total) : '–'}
                // The count opens every record counted
                href={f.name === null ? listWith(asked) : undefined}
              />
            ))}
          </SimpleGrid>
          <DataGrid<Row>
            records={sorted}
            idAccessor={(r) => r.key ?? ''}
            minHeight={sorted.length ? undefined : 160}
            noRecordsText={t('empty.noResults')}
            pinFirstColumn
            sortStatus={sort ?? { columnAccessor: '', direction: 'asc' }}
            onSortStatusChange={(status) => setSorting({ by, status })}
            onRowClick={({ record }) => open(groupHref(record.key))}
            rowStyle={(r) => (groupHref(r.key) ? { cursor: 'pointer' } : undefined)}
            columns={[
              {
                accessor: 'label',
                title: groupingLabel(by),
                sortable: true,
                footer: total ? t('report.total') : undefined,
              },
              ...figures.map((f) => ({
                accessor: f.name ?? 'count',
                title: f.label,
                width: 140,
                textAlign: 'right' as const,
                sortable: true,
                render: (r: Row) => shown(f, r),
                footer: total ? shown(f, total) : undefined,
              })),
            ]}
          />
          {summary?.more && (
            <Text size="sm" c="dimmed" px={24} py="sm">
              {t('report.more', { count: MAX_GROUPS })}
            </Text>
          )}
        </div>
      )}
      <PrintArea printRef={printer.ref}>
        <PrintableTable
          title={title}
          subtitle={[periodText, `${t('report.groupBy')} ${groupingLabel(by)}`, filterText || t('report.allFilters')].join(' · ')}
          columns={[{ header: groupingLabel(by) }, ...figures.map((f) => ({ header: f.label, num: true }))]}
          rows={sorted.map((r) => [r.label, ...figures.map((f) => shown(f, r))])}
          foot={total && [t('report.total'), ...figures.map((f) => shown(f, total))]}
        />
      </PrintArea>
    </Page>
  )
}

/** A choice's place among its field's options; no value last */
const rank = (field: ResolvedField, key: string | null) => (key === null ? Number.MAX_SAFE_INTEGER : (field.options?.indexOf(key) ?? 0))

/** Rows in the order of a column picked: the group's words, or a figure */
function compare(sort: DataTableSortStatus<Row>, figures: Figure[], locale: string) {
  const sign = sort.direction === 'asc' ? 1 : -1
  const figure = figures.find((f) => (f.name ?? 'count') === sort.columnAccessor)
  return (a: Row, b: Row) => sign * (figure ? figure.of(a) - figure.of(b) : a.label.localeCompare(b.label, locale))
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <Tooltip label={label}>
      <ActionIcon size={40} onClick={onClick} aria-label={label} disabled={disabled}>
        {children}
      </ActionIcon>
    </Tooltip>
  )
}

/** The period, as a range or one of the usual ones: the report changes once both ends are picked */
function PeriodPicker({ from, to, onChange }: { from?: string; to?: string; onChange: (range: FilterValue) => void }) {
  const { t, i18n } = useTranslation()
  const locale = currentLocale(i18n)
  const [picking, setPicking] = useState<[string | null, string | null]>([from ?? null, to ?? null])
  useEffect(() => setPicking([from ?? null, to ?? null]), [from, to])
  const now = dayjs()
  const yesterday = iso(now.subtract(1, 'day'))
  const lastMonth = now.subtract(1, 'month')
  const presets: DatePickerPreset<'range'>[] = [
    { value: [today(), today()], label: t('time.today') },
    { value: [yesterday, yesterday], label: t('time.yesterday') },
    { value: [iso(now.subtract(6, 'day')), today()], label: t('list.last7Days') },
    { value: [iso(now.startOf('month')), today()], label: t('report.thisMonth') },
    { value: [iso(lastMonth.startOf('month')), iso(lastMonth.endOf('month'))], label: t('report.lastMonth') },
    { value: [iso(now.startOf('year')), today()], label: t('report.thisYear') },
  ]
  return (
    <DatePickerInput
      type="range"
      size="sm"
      radius="sm"
      flex="none"
      miw={240}
      aria-label={t('list.dateRange')}
      leftSection={<IconCalendar size={18} stroke={1.75} />}
      valueFormat={dateFormat(locale, 'medium')}
      presets={presets}
      maxDate={today()}
      allowSingleDateInRange
      value={picking}
      onChange={([start, end]) => {
        setPicking([start, end])
        if (start && end) onChange({ op: 'range', from: start, to: end })
      }}
    />
  )
}
